import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

// Crossfade only the stake's text. The surrounding frame, chart and clocks stay live.
export function StakeContent({
  state,
  children,
}: {
  state: string;
  children: ReactNode;
}) {
  const current = useRef<HTMLSpanElement>(null);
  const outgoing = useRef<HTMLSpanElement>(null);
  const last = useRef({ state, children });
  const [leaving, setLeaving] = useState<{
    state: string;
    children: ReactNode;
  } | null>(null);
  useLayoutEffect(() => {
    if (last.current.state !== state) setLeaving(last.current);
    last.current = { state, children };
  }, [state, children]);
  useLayoutEffect(() => {
    if (!leaving || !current.current || !outgoing.current) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const css = getComputedStyle(current.current);
    const number = (name: string, fallback: number) => {
      const value = Number.parseFloat(css.getPropertyValue('--card-' + name));
      return Number.isFinite(value) ? value : fallback;
    };
    const duration = Math.max(0, number('ms', 480));
    if (!duration || reduced.matches) {
      setLeaving(null);
      return;
    }
    const blur = number('blur', 8),
      scale = number('scale', 0.92),
      rise = number('rise', 28);
    const easing =
      css.getPropertyValue('--card-ease').trim() ||
      'cubic-bezier(0.4, 0, 0.2, 1)';
    const enter = current.current.animate(
      [
        {
          opacity: 0,
          filter: `blur(${blur}px)`,
          transform: `translateY(${rise}px) scale(${scale})`,
        },
        { opacity: 1, filter: 'blur(0px)', transform: 'none' },
      ],
      { duration, easing },
    );
    const exit = outgoing.current.animate(
      [
        { opacity: 1, filter: 'blur(0px)', transform: 'none' },
        {
          opacity: 0,
          filter: `blur(${blur}px)`,
          transform: `translateY(${-rise}px) scale(${scale})`,
        },
      ],
      { duration: duration * 0.65, easing, fill: 'forwards' },
    );
    enter.id = 'stake-content-enter';
    exit.id = 'stake-content-exit';
    let cancelled = false;
    const finish = () => {
      if (!cancelled) setLeaving(null);
    };
    enter.finished.then(finish, () => {});
    reduced.addEventListener('change', finish);
    return () => {
      cancelled = true;
      enter.cancel();
      exit.cancel();
      reduced.removeEventListener('change', finish);
    };
  }, [leaving]);
  return (
    <span className="stake-content">
      {leaving && (
        <span className="stake-content-previous" aria-hidden="true" inert>
          <span ref={outgoing} className="stake-content-layer">
            {leaving.children}
          </span>
        </span>
      )}
      <span ref={current} className="stake-content-layer">
        {children}
      </span>
    </span>
  );
}
